import React, { useState } from 'react';

import { Button, Flex, ScrollArea, Table, Tabs, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import {
  AdminFeatureFlag,
  useGetAllAdminFlagsQuery,
  useUpdateAdminFeatureFlagsMutation
} from '@/store/slices/api/featureFlagsApiSlice';
import { LogoIcon } from '@/assets/icons';

import LoadingPage from '@/pages/common/LoadingPage';
import { LabelAndValue } from '@/types/custom';
import Dropdown from '@/ui/Dropdown';

import styles from './featureFlags.module.scss';

dayjs.extend(advancedFormat);

const SELECT_OPTIONS: LabelAndValue[] = [
  { label: 'Off', value: '0' },
  { label: 'Backend Only', value: '1' },
  { label: 'Testing', value: '2' },
  { label: 'Alpha', value: '3' },
  { label: 'Beta', value: '4' },
  { label: 'All', value: '5' }
];

const COLUMNS = [
  { Header: 'Name', accessor: 'displayName', width: '20%' },
  { Header: 'Description', accessor: 'displaySummary', width: '35%' },
  { Header: 'Key', accessor: 'shortCode', width: '8%' },
  { Header: 'Value', accessor: 'setting', width: '20%' },
  { Header: 'Last edited by', accessor: 'updated_by', width: '17%' }
];

const getOptionFromValue = (value: string): LabelAndValue =>
  SELECT_OPTIONS.find((opt) => opt.value === value) || { label: 'Unknown', value };

const FeatureFlagPage: React.FC = () => {
  const { data: featureFlags, isLoading } = useGetAllAdminFlagsQuery();
  const [selectedValues, setSelectedValues] = useState<Record<string, LabelAndValue>>({});

  // Helper to reset all changes
  const handleCancel = () => {
    setSelectedValues({});
    notifications.show({
      title: 'Cancelled',
      message: 'All changes have been reset.',
      color: 'gray'
    });
  };
  const [updateFlags] = useUpdateAdminFeatureFlagsMutation();

  if (isLoading || !featureFlags) {
    return <LoadingPage />;
  }

  const processedData =
    featureFlags?.data?.map((flag) => ({
      ...flag,
      updated_by: `${flag.updated_by} (${dayjs(flag.updatedAt).format('MMMM Do, YYYY')})`
    })) || [];

  const handleValueChange = (rowIndex: number, option: LabelAndValue | null) => {
    if (!option) return;

    setSelectedValues((prev) => ({
      ...prev,
      [rowIndex]: option
    }));
  };

  const getChangedFlags = () =>
    processedData
      .map((row, index) => {
        const selected = selectedValues[index];
        if (selected && selected.value !== String(row.setting)) {
          return {
            id: row.id,
            setting: selected.value
          };
        }
        return null;
      })
      .filter(Boolean) as { id: string; setting: string }[];

  const getChangedFlagsAndUpdate = async () => {
    const changedFlags = getChangedFlags();
    if (changedFlags.length === 0) {
      notifications.show({
        title: 'No Changes',
        message: 'There are no Feature Flags to Update.',
        color: 'yellow'
      });
      return;
    }
    try {
      await updateFlags(changedFlags).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Feature flags updated successfully.',
        color: 'green'
      });
      setSelectedValues({}); // Reset after save
    } catch (error) {
      console.error('Failed to update feature flags:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update feature flags.',
        color: 'red'
      });
    }
  };

  const renderEmptyState = () => (
    <Table.Tbody>
      <Table.Tr>
        <Table.Td
          colSpan={COLUMNS.length}
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            height: '60vh'
          }}>
          <LogoIcon width={100} height={100} />
          <Text size="lg" mt="md">
            There are currently no feature flags
          </Text>
        </Table.Td>
      </Table.Tr>
    </Table.Tbody>
  );

  const renderTableBody = () => (
    <Table.Tbody>
      {processedData.map((row: AdminFeatureFlag, index: number) => (
        <Table.Tr key={row.id || index}>
          {COLUMNS.map((column) => (
            <Table.Td key={column.accessor} style={{ width: column.width }}>
              {column.accessor === 'setting' ? (
                <Dropdown
                  options={SELECT_OPTIONS}
                  value={selectedValues[index] ?? getOptionFromValue(String(row.setting))}
                  onChange={(option) => handleValueChange(index, option)}
                  placeholder="Select value"
                />
              ) : (
                String(row[column.accessor as keyof AdminFeatureFlag] ?? '—')
              )}
            </Table.Td>
          ))}
        </Table.Tr>
      ))}
    </Table.Tbody>
  );

  const hasChanges = getChangedFlags().length > 0;

  return (
    <Flex direction="column" h="100vh">
      <Tabs defaultValue="flags" flex={1}>
        <Tabs.List>
          <Tabs.Tab value="flags" fz="h4">
            Feature Flags
          </Tabs.Tab>
        </Tabs.List>

        <ScrollArea flex={1} style={{ width: '100%' }}>
          <Tabs.Panel value="flags" pt="md">
            <Table mb={'75px'} highlightOnHover striped withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  {COLUMNS.map((column) => (
                    <Table.Th key={column.accessor} style={{ width: column.width }}>
                      {column.Header}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>

              {processedData.length === 0 ? renderEmptyState() : renderTableBody()}
            </Table>
          </Tabs.Panel>
        </ScrollArea>
      </Tabs>

      <Flex className={styles.footer} justify="flex-end" gap="md" p="md">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={getChangedFlagsAndUpdate} disabled={!hasChanges}>
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

export default FeatureFlagPage;
